import { useState } from "react";
import { useNavigate } from "react-router-dom";

const PLANS = [
  {
    id: "basic",
    name: "Basic",
    monthly: "$9.99",
    annual: "$7.99",
    period: "/month",
    tagline: "Great for getting started",
    highlighted: false,
    features: [
      { text: "Up to 10 job applications/month", ok: true },
      { text: "Basic capability matching", ok: true },
      { text: "Job board access", ok: true },
      { text: "Availability toggle", ok: true },
      { text: "AI resume generation", ok: false },
      { text: "Auto-fill applications", ok: false },
      { text: "Priority job matching", ok: false },
      { text: "Profile optimization", ok: false },
    ],
    cta: "Start Basic",
  },
  {
    id: "pro",
    name: "Pro",
    monthly: "$29.99",
    annual: "$23.99",
    period: "/month",
    tagline: "For serious earners",
    highlighted: true,
    badge: "Most Popular",
    features: [
      { text: "Unlimited job applications", ok: true },
      { text: "AI-powered capability matching", ok: true },
      { text: "Full marketplace access", ok: true },
      { text: "Availability toggle + live alerts", ok: true },
      { text: "AI resume + cover letter generation", ok: true },
      { text: "Semi-automated auto-fill apply", ok: true },
      { text: "Priority job matching", ok: true },
      { text: "Profile optimization included", ok: true },
    ],
    cta: "Start Free Trial",
  },
  {
    id: "employer",
    name: "Employer",
    monthly: "Custom",
    annual: "Custom",
    period: "",
    tagline: "For teams & businesses",
    highlighted: false,
    features: [
      { text: "Unlimited job postings", ok: true },
      { text: "Real-time worker matching", ok: true },
      { text: "Urgency job broadcasting", ok: true },
      { text: "Candidate management dashboard", ok: true },
      { text: "Team accounts", ok: true },
      { text: "API access", ok: true },
      { text: "10–20% commission on completion", ok: true },
      { text: "Dedicated account manager", ok: true },
    ],
    cta: "Contact Sales",
  },
];

const ADDONS = [
  { icon: "ri-user-star-line", label: "Profile Optimization", price: "$19 one-time" },
  { icon: "ri-file-text-line", label: "Professional Resume Rewrite", price: "$49 one-time" },
  { icon: "ri-rocket-2-line", label: "Priority Listing Boost", price: "$9.99/week" },
  { icon: "ri-graduation-cap-line", label: "Upskill Course Bundle", price: "$29 one-time" },
];

export default function PricingPlans() {
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");
  const [selectedPlan, setSelectedPlan] = useState("pro");
  const navigate = useNavigate();

  const handleCta = (plan: typeof PLANS[0]) => {
    if (plan.id === "employer") {
      window.location.href = "mailto:sales@gigncash.com";
      return;
    }
    navigate(`/checkout?plan=${plan.id}&billing=${billing}`);
  };

  return (
    <>
      {/* Billing Toggle */}
      <div className="flex justify-center mb-10">
        <div className="bg-gray-100 rounded-full p-1 flex gap-1">
          <button
            onClick={() => setBilling("monthly")}
            className={`px-5 py-2 text-sm font-bold rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
              billing === "monthly" ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBilling("annual")}
            className={`px-5 py-2 text-sm font-bold rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
              billing === "annual" ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Annual
            <span className="ml-2 bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">-20%</span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`relative rounded-2xl border p-7 flex flex-col gap-5 transition-all duration-200 ${
              plan.highlighted
                ? "border-emerald-500 bg-emerald-500"
                : "border-gray-200 bg-white hover:border-emerald-200"
            }`}
          >
            {plan.badge && (
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <span className="bg-amber-500 text-white text-xs font-extrabold px-4 py-1.5 rounded-full whitespace-nowrap">
                  {plan.badge}
                </span>
              </div>
            )}

            <div>
              <div className={`text-xs font-bold uppercase tracking-widest mb-1 ${plan.highlighted ? "text-emerald-200" : "text-gray-400"}`}>
                {plan.name}
              </div>
              <div className="flex items-baseline gap-1">
                <span className={`text-4xl font-black ${plan.highlighted ? "text-white" : "text-gray-900"}`}>
                  {billing === "monthly" ? plan.monthly : plan.annual}
                </span>
                {plan.period && (
                  <span className={`text-sm font-medium ${plan.highlighted ? "text-emerald-200" : "text-gray-400"}`}>
                    {plan.period}
                  </span>
                )}
              </div>
              <p className={`text-sm mt-1 ${plan.highlighted ? "text-emerald-100" : "text-gray-500"}`}>{plan.tagline}</p>
              {billing === "annual" && plan.id !== "employer" && (
                <p className={`text-xs mt-1 font-semibold ${plan.highlighted ? "text-emerald-200" : "text-emerald-600"}`}>
                  Billed annually — save 20%
                </p>
              )}
            </div>

            <ul className="flex flex-col gap-2.5 flex-1">
              {plan.features.map((f) => (
                <li key={f.text} className="flex items-start gap-2.5">
                  <span className={`mt-0.5 w-4 h-4 flex items-center justify-center rounded-full text-xs shrink-0 ${
                    f.ok
                      ? plan.highlighted ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700"
                      : plan.highlighted ? "bg-white/10 text-emerald-300" : "bg-gray-100 text-gray-300"
                  }`}>
                    <i className={f.ok ? "ri-check-line" : "ri-close-line"} />
                  </span>
                  <span className={`text-sm leading-relaxed ${
                    f.ok
                      ? plan.highlighted ? "text-white" : "text-gray-700"
                      : plan.highlighted ? "text-emerald-300/60" : "text-gray-300"
                  }`}>
                    {f.text}
                  </span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleCta(plan)}
              className={`mt-auto py-3 font-bold text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap ${
                plan.highlighted
                  ? "bg-white text-emerald-700 hover:bg-emerald-50"
                  : "border border-gray-300 text-gray-700 hover:border-emerald-500 hover:text-emerald-600"
              }`}
            >
              {plan.cta}
            </button>
          </div>
        ))}
      </div>

      {/* Add-ons */}
      <div className="bg-[#fafafa] rounded-2xl border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
          <div className="flex-1">
            <h3 className="font-extrabold text-gray-900 text-base mb-1">Optional Add-ons</h3>
            <p className="text-sm text-gray-500">Supercharge your profile and earning potential with premium upgrades.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {ADDONS.map((a) => (
              <div key={a.label} className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-2.5 cursor-pointer hover:border-emerald-300 transition-colors">
                <i className={`${a.icon} text-emerald-600`} />
                <div>
                  <div className="text-xs font-bold text-gray-800">{a.label}</div>
                  <div className="text-xs text-gray-500">{a.price}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
